import { useMemo, useCallback } from "react";
import { Schema, FieldType } from "../../../toolympus/hooks/useSchema";
import { useLoadedData } from "../../../toolympus/hooks/useLoadedData";
import { useEditItem2 } from "../../../toolympus/api/useNewItem";
import { apiFetch } from "../../../toolympus/api/core";
import { CopTreeNode } from "./useCopTreeNodes";

export interface CopDocumentNodeLink {
  _id: number;
  doc_id: number;
  node_id: number;
  anchor_id?: string | null;
  relation_type?: string | null;
  metadata?: any;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
}

export const CopDocumentNodeLinkSchema: Schema = {
  _id: { label: "ID", type: FieldType.number },
  doc_id: { label: "Document ID", type: FieldType.number },
  node_id: { label: "Node ID", type: FieldType.number },
  relation_type: {
    label: "Relation type",
    type: FieldType.dictionarySelect,
    dictionary: "CopTreeDocumentRelationType",
  },
  anchor_id: { label: "Anchor ID" },
  created_by: { label: "Created by", dictionary: "Users", type: FieldType.dictionarySelect },
  created_at: { label: "Created at", type: FieldType.datetime },
};

export interface TreeNodeWithChildren extends CopTreeNode {
  children: TreeNodeWithChildren[];
}

export interface LinkedDocNodeItem {
  link: CopDocumentNodeLink;
  node: CopTreeNode;
}

const TreeNodesApiPath = "/api/cop-decision/tree/node";
const DocLinksApiPath = "/api/cop-decision/tree/doc-node-link";

export const useCopDocumentNodeLinks = (decisionId?: number | null) => {
  // 1. Load all tree nodes
  const treeData = useLoadedData<CopTreeNode[]>(TreeNodesApiPath, []);

  // 2. Load links for this document
  const linksUrl = decisionId && decisionId > 0
    ? `${DocLinksApiPath}?doc_id=${decisionId}`
    : null;
  const linksData = useLoadedData<CopDocumentNodeLink[]>(linksUrl || "", [], !!linksUrl);

  // 3. Build fast lookup maps and hierarchy
  const { nodesById, rootNodes, linkedNodes, linkedNodeIds } = useMemo(() => {
    const nodesById = new Map<number, CopTreeNode>();
    treeData.data.forEach(n => nodesById.set(n._id, n));

    const linkedNodeIds = new Set<number>();
    const linkedNodes: LinkedDocNodeItem[] = [];

    linksData.data.forEach(link => {
      linkedNodeIds.add(link.node_id);
      const node = nodesById.get(link.node_id);
      if (node) {
        linkedNodes.push({ link, node });
      }
    });

    // Build hierarchical tree
    const childrenByParentKey = new Map<string, TreeNodeWithChildren[]>();
    const rawRootNodes: TreeNodeWithChildren[] = [];

    treeData.data.forEach(n => {
      const treeNodeItem: TreeNodeWithChildren = { ...n, children: [] };
      if (!n.path || n.path.trim() === "") {
        rawRootNodes.push(treeNodeItem);
      } else {
        const list = childrenByParentKey.get(n.path) || [];
        list.push(treeNodeItem);
        childrenByParentKey.set(n.path, list);
      }
    });

    const attachChildren = (parent: TreeNodeWithChildren) => {
      const parentKey = parent.path
        ? `${parent.path}.${parent._id}`
        : `${parent._id}`;
      const children = childrenByParentKey.get(parentKey) || [];
      children.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      parent.children = children;
      children.forEach(attachChildren);
    };

    rawRootNodes.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    rawRootNodes.forEach(attachChildren);

    return {
      nodesById,
      rootNodes: rawRootNodes,
      linkedNodes,
      linkedNodeIds,
    };
  }, [treeData.data, linksData.data]);

  // 4. Mutations
  const addLink = useCallback(
    async (payload: {
      node_id: number;
      relation_type?: string | null;
      anchor_id?: string | null;
    }) => {
      if (!decisionId) return;
      await apiFetch<CopDocumentNodeLink>(DocLinksApiPath, "post", {
        doc_id: decisionId,
        node_id: payload.node_id,
        relation_type: payload.relation_type || null,
        anchor_id: payload.anchor_id || null,
      });
      await linksData.reload();
    },
    [decisionId, linksData]
  );

  const editLink = useEditItem2<CopDocumentNodeLink>({
    save: async item => {
      if (!item) return item!;
      await apiFetch<CopDocumentNodeLink>(`${DocLinksApiPath}/${item._id}`, "put", {
        relation_type: item.relation_type || null,
        anchor_id: item.anchor_id || null,
      });
      await linksData.reload();
      return item;
    },
  });

  const removeLink = useCallback(
    async (linkId: number) => {
      await apiFetch<CopDocumentNodeLink>(`${DocLinksApiPath}/${linkId}`, "delete");
      await linksData.reload();
    },
    [linksData]
  );

  return {
    isLoading: treeData.isLoading || linksData.isLoading,
    allNodes: treeData.data,
    nodesById,
    rootNodes,
    linkedNodes,
    linkedNodeIds,
    addLink,
    editLink,
    removeLink,
    reloadLinks: linksData.reload,
  };
};